import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { accessoriesApi, suppliersApi } from '../api';
import { useFetch } from '../hooks/useFetch';
import toast from 'react-hot-toast';

function AccessoryModal({ acc, onClose, onSaved }) {
  const [form, setForm] = useState(acc ? { name: acc.name, category: acc.category, unit: acc.unit, quantityAvailable: acc.quantityAvailable, costPerUnit: acc.costPerUnit, lowStockThreshold: acc.lowStockThreshold, supplier: acc.supplier || '' } : { name: '', category: 'fastener', unit: 'piece', quantityAvailable: '', costPerUnit: '', lowStockThreshold: 10, supplier: '' });
  const [loading, setLoading] = useState(false);

  const { data: suppliers } = useFetch(() => suppliersApi.getAll(), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // ✅ Validation
    if (!form.name) {
      return toast.error("Name is required");
    }

    if (!form.category) {
      return toast.error("Category is required");
    }

    if (!form.unit) {
      return toast.error("Unit is required");
    }

    if (!form.quantityAvailable) {
      return toast.error("Quantity is required");
    }

    if (Number(form.quantityAvailable) < 0) {
      return toast.error("Quantity cannot be negative");
    }

    if (!form.costPerUnit) {
      return toast.error("Cost per unit is required");
    }

    if (Number(form.costPerUnit) < 0) {
      return toast.error("Cost cannot be negative");
    }

    setLoading(true);

    try {
      if (acc) await accessoriesApi.update(acc.id, form);
      else await accessoriesApi.create(form);
      toast.success(acc ? 'Updated' : 'Created'); onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header"><h3>{acc ? 'Edit Accessory' : 'New Accessory'}</h3><button className="btn btn-secondary btn-sm btn-icon" onClick={onClose}>✕</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row form-row-2">
              <div className="form-group"><label className="form-label">Name *</label><input className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Button, Zipper..." /></div>
              <div className="form-group"><label className="form-label">Category *</label>
                <select className="form-control" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option value="fastener">Fastener</option><option value="lining">Lining</option><option value="trim">Trim</option><option value="thread">Thread</option><option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="form-row form-row-3">
              <div className="form-group"><label className="form-label">Unit *</label><input className="form-control" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="piece, meter..." /></div>
              <div className="form-group"><label className="form-label">Quantity *</label><input type="number" className="form-control" step="0.01" min="0" value={form.quantityAvailable} onChange={(e) => setForm({ ...form, quantityAvailable: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Cost/Unit (₹) *</label><input type="number" className="form-control" step="0.01" min="0" value={form.costPerUnit} onChange={(e) => setForm({ ...form, costPerUnit: e.target.value })} /></div>
            </div>
            <div className="form-row form-row-2">
              <div className="form-group"><label className="form-label">Low Stock Threshold</label><input type="number" className="form-control" step="0.01" min="0" value={form.lowStockThreshold} onChange={(e) => setForm({ ...form, lowStockThreshold: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Supplier</label>
                <select className="form-control" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })}>
                  <option value="">Select Supplier</option>
                  {suppliers?.suppliers?.map((supplier) => (
                    <option key={supplier.id} value={supplier.name}>{supplier.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : (acc ? 'Update' : 'Create')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RestockModal({ acc, onClose, onSaved }) {
  const [qty, setQty] = useState('');
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Validation
    if (!qty) {
      return toast.error("Quantity is required");
    }

    if (Number(qty) <= 0) {
      return toast.error("Quantity must be greater than 0");
    }
    setLoading(true);
    try { await accessoriesApi.restock(acc.id, qty); toast.success('Restocked!'); onSaved(); onClose(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    setLoading(false);
  };
  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 380 }}>
        <div className="modal-header"><h3>Restock: {acc.name}</h3><button className="btn btn-secondary btn-sm btn-icon" onClick={onClose}>✕</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="alert" style={{ background: 'var(--accent-glow)', color: 'var(--accent-light)', borderColor: 'rgba(99,102,241,0.2)', marginBottom: '1rem' }}>
              Current: <strong>{Number(acc.quantityAvailable).toFixed(2)} {acc.unit}</strong>
            </div>
            <div className="form-group"><label className="form-label">Add Quantity *</label><input type="number" className="form-control" autoFocus step="0.01" min="0.01" value={qty} onChange={(e) => setQty(e.target.value)} /></div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-success" disabled={loading}>{loading ? 'Saving...' : 'Add Stock'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Accessories() {
  const navigate = useNavigate();
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('accessories');

  const fetchAcc = useCallback(() => accessoriesApi.getAll({ limit: 100 }), []);
  const fetchTemplates = useCallback(() => accessoriesApi.getTemplates(), []);
  const { data: accData, loading: aLoading, refetch: refetchA } = useFetch(fetchAcc, []);
  const { data: templates, loading: tLoading, refetch: refetchT } = useFetch(fetchTemplates, []);
  const accessories = accData?.accessories || accData || [];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Accessories</h1>
        <div className="flex gap-2">
          {tab === 'accessories' && <button className="btn btn-primary" onClick={() => { setSelected(null); setModal('acc'); }}>+ New Accessory</button>}
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        <button className={`btn ${tab === 'accessories' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('accessories')}>Accessories</button>
        <button className={`btn ${tab === 'templates' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('templates')}>Templates</button>
      </div>

      {tab === 'accessories' && (
        <div className="card">
          {aLoading ? <div className="empty-state"><div className="spinner" /></div> : (
            <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
              <table>
                <thead><tr><th>Name</th><th>Category</th><th>Available</th><th>Cost/Unit</th><th>Low Stock?</th><th>Supplier</th><th>Actions</th></tr></thead>
                <tbody>
                  {accessories.length === 0 ? (
                    <tr><td colSpan={7}><div className="empty-state"><div className="empty-icon">🧵</div><p>No accessories</p></div></td></tr>
                  ) : accessories.map((a) => (
                    <tr key={a.id}>
                      <td className="font-semibold">{a.name}</td>
                      <td><span className="badge badge-stitching">{a.category}</span></td>
                      <td className={Number(a.quantityAvailable) <= Number(a.lowStockThreshold) ? 'text-danger font-semibold' : 'text-success font-semibold'}>{Number(a.quantityAvailable).toFixed(2)} {a.unit}</td>
                      <td>₹{Number(a.costPerUnit).toFixed(2)}</td>
                      <td>{Number(a.quantityAvailable) <= Number(a.lowStockThreshold) ? <span className="badge badge-cancelled">⚠ Low</span> : <span className="badge badge-ready">OK</span>}</td>
                      <td className="text-sm text-muted">{a.supplier || '—'}</td>
                      <td>
                        <div className="flex gap-2">
                          <button className="btn btn-info btn-sm" onClick={() => navigate(`/accessories/${a.id}`)}>View</button>
                          <button className="btn btn-success btn-sm" onClick={() => { setSelected(a); setModal('restock'); }}>+ Stock</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => { setSelected(a); setModal('acc'); }}>Edit</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'templates' && (
        <div className="grid-3">
          {tLoading ? <div className="empty-state"><div className="spinner" /></div> :
            (templates || []).map((t) => (
              <div key={t.id} className="card">
                <div className="card-header">
                  <div>
                    <div className="font-semibold">{t.name}</div>
                    <div className="text-xs text-muted">{t.garmentType}</div>
                  </div>
                  <span className={`badge ${t.isActive ? 'badge-ready' : 'badge-cancelled'}`}>{t.isActive ? 'Active' : 'Inactive'}</span>
                </div>
                <div className="card-body">
                  {(t.items || []).map((item) => (
                    <div key={item.id} className="flex justify-between items-center" style={{ padding: '0.4rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                      <span>{item.accessory?.name}</span>
                      <span className="text-muted">{item.quantityRequired} {item.accessory?.unit}</span>
                    </div>
                  ))}
                  {t.items?.length === 0 && <p className="text-sm text-muted">No items in this template</p>}
                </div>
              </div>
            ))
          }
          {(!tLoading && (!templates || templates.length === 0)) && <div className="empty-state" style={{ gridColumn: '1/-1' }}><div className="empty-icon">📋</div><p>No templates yet</p></div>}
        </div>
      )}

      {modal === 'acc' && <AccessoryModal acc={selected} onClose={() => setModal(null)} onSaved={refetchA} />}
      {modal === 'restock' && <RestockModal acc={selected} onClose={() => setModal(null)} onSaved={refetchA} />}
    </div>
  );
}
